import Select from "react-select";

import type Device from "../../models/Device";
import type KnxProductData from "../../KnxProductdata";
import { ValueLevels } from "../../models/KnxInstanceData";
import { ChannelTypeProperty } from "../../models/Channel";
import { ChannelType } from "../../models/ChannelType";
import { isUiModeExpert, isUiModeProductAdmin } from "../../uiMode";
import { isDevelopmentEnvironment } from "../../estada-config";
import { useOnChangeWatcher } from "../../models/useOnChangeWatcher";
import { isParameterType, isTypeFloat, isTypeNumber, isTextType } from "../../Types";
import { calculateKnxIndicationColor } from "./KnxPropertyIndicator";
import { showToast } from "../Toasts";

export interface KnxParameterValueListEntry {
  key: string;
  value: string;
  valueEN: string | undefined;
}

//
// Hilfsfunktion: baut die `valueList` aus dem ParameterType auf.
//
// Wird im Parent (KnxParameterViewer) für KnxPropertyViewAdminButton benötigt
// und ist dort weiterhin sichtbar / erweiterbar, ohne dass KnxProperty2 dafür
// einen Callback braucht.
//
export function buildKnxParameterValueList(
  parmeterType: any,
  knxProductData: KnxProductData | undefined,
  device: Device,
): KnxParameterValueListEntry[] {
  const list: KnxParameterValueListEntry[] = [];
  const enums = parmeterType?.TR || parmeterType?.TypeRestriction;
  if (!enums?.items || !knxProductData) {
    return list;
  }
  for (const i in enums.items) {
    const e = enums.items[i];
    let optText = e.a.Text;
    const optText1 = knxProductData.languageMapping.getTranslation(e.a.Id, "Text");
    if (optText1) {
      optText = optText1;
    }
    const textEN = device.template?.knxProductData.getTranslatedProperty("Text", e, true);
    list.push({ key: e.a.Value, value: optText, valueEN: textEN });
  }
  return list;
}

export interface KnxProperty2Props {
  className?: string;
  device: Device;
  item: any;
  refId: string;
  instanceId: string;
  chapterId?: string;
  isCell?: boolean;
  onChange?: () => void;
}

interface XChecboxProps {
  readonlyAccess: boolean;
  refId: string;
  instanceId: string;
  xid: string;
  currentValue: any;
  onValueChange: (checked: boolean) => void;
  indicationColor: string | undefined;
}

function XChecbox(props: XChecboxProps): any {
  const { readonlyAccess, refId, instanceId, xid, currentValue, onValueChange, indicationColor } = props;

  return (
    <div className="knxCheckbox">
      <input
        type="checkbox"
        key={"chk-" + currentValue}
        disabled={readonlyAccess}
        {...(isDevelopmentEnvironment() ? { "data-id": refId, "data-instance-id": instanceId } : {})}
        style={{
          width: "20px",
          height: "30px",
          padding: "5px",
          border: `2px solid ${indicationColor}`,
          borderRadius: "3px",
          accentColor: "grey",
        }}
        checked={currentValue == "1"}
        id={xid}
        onChange={(ev) => {
          onValueChange(ev.target.checked);
        }}
      />
    </div>
  );
}

export default function KnxProperty2(props: KnxProperty2Props): any {
  const { className, device, item, refId, instanceId, chapterId, onChange, isCell } = props;

  useOnChangeWatcher(device.knxInstanceData, () => {
    return true;
  });

  const knxProductData = device.template?.knxProductData;
  const knxInstanceData = device.knxInstanceData;

  let currentValue = knxInstanceData.getValue(refId, instanceId);
  const currentValueExt = knxInstanceData.getExtendedData(refId, instanceId);
  const propertyIsSetByEstada = currentValueExt?.mbe?.isSet;

  const access = item.a.Access;
  const readonlyAccess = access == "Read";

  const ParameterTypeId = item.a.ParameterType;
  const parmeterType = knxProductData?.globalMap.get(ParameterTypeId);

  const pameterRestriction = parmeterType?.TR || parmeterType?.TypeRestriction;
  const pameterEnumerations = pameterRestriction;

  const isCheckbox = parmeterType?.TypeNumber?.a.UIHint == "CheckBox";
  const isColorSelection = !!parmeterType?.TypeColor;

  const xid = instanceId != "" ? instanceId + "|" + refId : refId;

  function parseLocaleFloat(input: any): number | undefined {
    const language = state.settings.uiLanguage;
    const parts = new Intl.NumberFormat(language).formatToParts(12345.6);
    const groupSeparator = parts.find((p) => p.type == "group")?.value || ",";
    const decimalSeparator = parts.find((p) => p.type == "decimal")?.value || ".";

    let normalized = `${input}`.trim();
    normalized = normalized.replace(/\s|\u00A0/g, "");

    if (groupSeparator) {
      normalized = normalized.split(groupSeparator).join("");
    }
    if (decimalSeparator != ".") {
      normalized = normalized.split(decimalSeparator).join(".");
    }

    const parsed = Number(normalized);
    if (Number.isNaN(parsed) || !Number.isFinite(parsed)) {
      return undefined;
    }
    return parsed;
  }

  function setValue(
    valueLevel: ValueLevels,
    newValue: any,
    id: string,
    instId: string,
    chId: string,
    supressChangeEvent?: boolean,
  ): void {
    if (isFloatType) {
      // Convert locale-formatted float input (e.g. "1,23") to canonical format ("1.23").
      const parsed = parseLocaleFloat(newValue);
      if (parsed != undefined) {
        newValue = `${parsed}`;
      }
    }

    const lastValue = knxInstanceData.getValue(id, instId);

    if (lastValue != newValue) {
      knxInstanceData.setValue(ValueLevels.interactive, newValue, id, instId, "", supressChangeEvent);

      if (knxInstanceData.isReEvalId(id, instId)) {
        device.raiseOnChange("parameter");
      }

      onChange?.();
    }
  }

  let isNumberType = false;
  let isFloatType = false;
  const minWidth = isUiModeProductAdmin() ? 100 : undefined;

  if (isParameterType(parmeterType)) {
    const TN = parmeterType.items[0];
    if (isTypeNumber(TN)) {
      isNumberType = true;
    }

    if (isTypeFloat(TN)) {
      isFloatType = true;
      const n = Number(currentValue);
      if (n != undefined) {
        const language = state.settings.uiLanguage;
        const localized = n.toLocaleString(language, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
        currentValue = localized;
      }
    }

    if (isTextType(TN)) {
      const SizeInBit = (TN as any).a.SizeInBit;
      if (SizeInBit && SizeInBit > 0) {
        const maxLength = Math.floor(SizeInBit / 8);
        knxInstanceData.setExtendedData({ maxLength: maxLength }, refId, instanceId);
      }
    }
  }

  let indicationColor: string | undefined = undefined;

  indicationColor = calculateKnxIndicationColor({
    device,
    refId,
    instanceId,
  });

  let borderLeft: string | undefined = undefined;
  if (indicationColor) {
    borderLeft = `3px solid ${indicationColor}`;
  }

  let inputElement: any = null;

  if (isCheckbox) {
    console.log("render prop2 checkbox: " + xid + "  " + currentValue);
    inputElement = (
      <XChecbox
        readonlyAccess={readonlyAccess}
        refId={refId}
        instanceId={instanceId}
        xid={xid}
        currentValue={currentValue}
        onValueChange={(newValue) => {
          setValue(ValueLevels.interactive, newValue ? "1" : "0", refId, instanceId, chapterId || "");
        }}
        indicationColor={indicationColor}
      />
    );
  } else if (pameterEnumerations) {
    const options: any = [];

    for (const i in pameterEnumerations.items) {
      const pameterEnumeration = pameterEnumerations.items[i];

      const opt = pameterEnumeration;
      const optValue = opt.a.Value;
      let optText = opt.a.Text;
      const optIcon = opt.a.Icon;
      const optText1 = knxProductData?.languageMapping.getTranslation(pameterEnumeration.a.Id, "Text");
      if (optText1) {
        optText = optText1;
      }

      if (optIcon) {
        const filename = optIcon + ".png";
        const iconFileSrc = knxProductData?.iconFileSrc[filename];
        options.push({
          value: optValue,
          label: (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
              {/* 
              MWE 21.5.2024: Die Icon von GVS haben eine Höhe von 52px 
              und sehen auch nur mit dieser OriginalGröße korrekt aus. */}
              <img
                src={iconFileSrc}
                style={{ width: "auto", height: "30px", objectFit: "contain" }}
                onContextMenu={(e) => {
                  if (isUiModeProductAdmin()) {
                    console.log("onContextMenu", e);
                    e.stopPropagation();
                  }
                }}
              />
              <span style={{ marginLeft: "5px" }}>{optText || ""}</span>
            </div>
          ),
        });
      } else {
        options.push({
          value: optValue,
          label: optText,
        });
      }
    }

    if (readonlyAccess) {
      inputElement = (
        <div style={{ borderLeft, height: "30px" }}>{options.find((it: any) => it.value == currentValue)?.label}</div>
      );
    } else if (propertyIsSetByEstada && !isUiModeExpert()) {
      inputElement = (
        <div style={{ borderLeft, height: "30px" }}>{options.find((it: any) => it.value == currentValue)?.label}</div>
      );
    } else {
      const InitalValue = options.find((it: any) => it.value == currentValue);

      // inputElement = (
      //   <select id="selectTest" defaultValue={InitalValue} className={`knxSelect`}>
      //     {options.map((opt: any) => (
      //       <option key={opt.value} value={opt.value}>
      //         {opt.label}
      //       </option>
      //     ))}
      //   </select>
      // );

      inputElement = (
        <Select
          name={xid}
          key={`${xid}-${currentValue}`}
          className={`knxSelect autoHeight ${item.a.Cell ? "cell" : ""} ${className || ""}`}
          {...(isDevelopmentEnvironment() ? { "data-id": refId, "data-instance-id": instanceId } : {})}
          styles={{
            menuPortal: (base) => ({
              ...base,
              zIndex: 9999,
            }),
            menu: (base) => ({
              ...base,
              zIndex: 9999,
            }),
            control: (baseStyles, _state) => ({
              ...baseStyles,
              borderColor: isCell ? "transparent" : "var(--colorLightGrey)",
              borderLeft,
              minHeight: "30px",
              minWidth: minWidth,
            }),
            indicatorSeparator: (baseStyles, _state) => ({
              ...baseStyles,
              width: "0",
            }),
            indicatorsContainer: (baseStyles, _state) => ({
              ...baseStyles,
              height: "30px",
              padding: "0px",
            }),
          }}
          id={xid}
          isDisabled={propertyIsSetByEstada && !isUiModeExpert()}
          options={options}
          defaultValue={InitalValue}
          onChange={(it: any) => {
            // async event, to give the user faster feedback on input
            if (it) {
              setTimeout(() => {
                const newValue = it.value;
                let level = ValueLevels.interactive;

                const extData = knxInstanceData.getExtendedData(refId, instanceId);

                if (extData?.isSceneItem) {
                  device.reEvalKnxMetadataNeeded = true;
                  device.knxInstanceData.raiseOnChange("sceneItemChanged");
                }

                const channelTypeProperty: ChannelTypeProperty = extData?.channelTypeProperty;
                if (channelTypeProperty) {
                  //
                  // Der ausgewähle Wert entspricht einem von Estada unterstützen Kanaltyp.
                  // Daher setzten wir hier den Kanaltyp "passend".
                  //
                  // Wenn wir keinen passenden Kanaltyp finden,
                  // dann setzen wir nur die Propery auf den ausgewählten Wert,
                  // der dann vom Benutzer bewußt gewählt wurde und abweichen kann.
                  //
                  // Das kann vorkommend zB. für ein "spezial Type" wie "Send",
                  // den der Benutzer ansonsten in der ButtonListe als "Switch" eingestellt hat
                  //
                  const channel = device.channels.findFirst(
                    (ch) =>
                      ch.knxChannel?.knxChannelTypeProperty?.id == refId &&
                      (ch.knxChannelTypeProperty?.instanceId || "") == instanceId,
                  );

                  let channelType = channelTypeProperty.mapValueToChannelType(newValue);
                  if (channel) {
                    if (channelType == undefined) {
                      channelType = ChannelType.FromKnxChannel;
                    }
                    //
                    // use lower level set to avoid any script calls, endless loop, etc.
                    //
                    channel._channelType = channelType;
                    channel.raiseOnChange("channelType");

                    if (channelType != ChannelType.FromKnxChannel) {
                      //
                      // MWE 7.10.2025
                      // Wenn wir hier ein Wert setzen,
                      // der auch in der ButtonListe ausgewählt hätte werden können
                      // setzten wir den level so,
                      // dass man in der Buttonliste den Kanaltyp wieder ändern kann
                      // und der Wert dann überschrieben wird
                      //

                      level = ValueLevels.device; // siehe auch Channel.ts: setKnxChannelTypePropertyValue

                      // Wert löschen / Level reseten, damit er unten mit level=device neu gesetzt werden kann
                      // iost relevant wenn wir von interactive "Send value" zb auf "Switch" wechseln
                      // und somit anschließend auch wieder in der Buttonliste den Kanaltyp ändern können sollen.
                      //
                      setValue(ValueLevels.delete, newValue, refId, instanceId, chapterId || "");
                    } else {
                      //
                      // anderenfalls haben wir eine Spezialfunktion wie "Send" o.ä. aufgewählt
                      // diese einstellung dürfen wir nicht einfach so überschreiben
                      // in dem Fall setzten wir den Level auf "interactive"
                      //
                      level = ValueLevels.interactive;
                    }
                  }
                }

                setValue(level, newValue, refId, instanceId, chapterId || "");
              }, 10);
            }
          }}
          isSearchable={false}
          isClearable={false}
        />
      );
    }
  } else {
    inputElement = (
      // we need a VERY dirty trick to force React to
      // move a modified value to the input during rerender.
      // React dosn't monitor "defaultvalue"
      // so changees in the defaultvalue will be lost
      // setting the key to the value, results in exchanging the whole input after a alue change
      <div className={`knxInput ${item.a.Cell ? "cell" : ""}  ${className || ""}`}>
        <div
          key={currentValue}
          id={xid}
          {...(isDevelopmentEnvironment() ? { "data-id": refId, "data-instance-id": instanceId } : {})}
          className="input"
          suppressContentEditableWarning={true}
          contentEditable={(!propertyIsSetByEstada || isUiModeExpert()) && !readonlyAccess}
          onKeyDown={(ev) => {
            if (ev.key == "Enter") {
              ev.preventDefault();
              ev.currentTarget.blur();
            }
          }}
          onBlur={(ev) => {
            let newValue = ev.target.innerText.trim();

            const extData = knxInstanceData.getExtendedData(refId, instanceId);
            if (extData?.maxLength) {
              if (newValue.length > extData.maxLength) {
                newValue = newValue.substring(0, extData.maxLength);
                ev.target.innerText = newValue;
                showToast({ text: `Eingabe auf maximale Länge von ${extData.maxLength} gekürzt` });
              }
            }

            setValue(ValueLevels.interactive, newValue, refId, instanceId, chapterId || "");

            if (extData?.isSceneItem) {
              device.reEvalKnxMetadataNeeded = true;
              device.knxInstanceData.raiseOnChange("sceneItemChanged");
            }
          }}
          style={{
            border: propertyIsSetByEstada && !isUiModeExpert() ? "transparent" : undefined,
            borderRadius: "3px",
            borderLeft: borderLeft,
            minWidth: minWidth,
          }}
        >
          {currentValue}
        </div>
        {item.a.SuffixText ? <span>&nbsp;{item.a.SuffixText}</span> : ""}
      </div>
    );
  }

  if (isColorSelection) {
    inputElement = (
      <div
        className={`knxInput ${item.a.Cell ? "cell" : ""}  ${className || ""}`}
        style={{ display: "flex", flexDirection: "row" }}
      >
        <input
          style={{ border: "none", height: "30px" }}
          {...(isDevelopmentEnvironment() ? { "data-id": refId, "data-instance-id": instanceId } : {})}
          key={currentValue?.toString()}
          type="color" // no itx
          defaultValue={currentValue}
          onChange={(ev) => {
            const newValue = ev.target.value;
            const div = ev.target.parentElement;
            const i = div?.children[0] as HTMLInputElement;
            if (i) {
              i.value = newValue;
            }

            setTimeout(() => {
              setValue(ValueLevels.interactive, newValue, refId, instanceId, chapterId || "");
            }, 10);
          }}
        />
        {inputElement}
      </div>
    );
  }

  return inputElement;
}
